import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateApartment } from "@/api/districts";
import { ApartmentCsvUpload } from "@/components/districts/apartment-csv-upload";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useHideElectricity } from "@/hooks/use-hide-electricity";
import { createApartmentSchema } from "@/lib/schemas/district.schema";
import { getDrfFieldErrors } from "@/utils/errors";

type CreateApartmentDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	districtId: number;
};

type Mode = "import" | "manual";

export function CreateApartmentDialog({
	open,
	onOpenChange,
	districtId,
}: CreateApartmentDialogProps) {
	const { t } = useTranslation();
	const [mode, setMode] = useState<Mode>("import");
	const [street, setStreet] = useState("");
	const [apartmentNumber, setApartmentNumber] = useState("");
	const [coldWaterRate, setColdWaterRate] = useState("");
	const [hotWaterRate, setHotWaterRate] = useState("");
	const [heatRate, setHeatRate] = useState("");
	const [consumptionRate, setConsumptionRate] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

	const hideElectricity = useHideElectricity();
	const create = useCreateApartment();

	useEffect(() => {
		if (open) {
			setMode("import");
			setStreet("");
			setApartmentNumber("");
			setColdWaterRate("");
			setHotWaterRate("");
			setHeatRate("");
			setConsumptionRate("");
			setFieldErrors({});
		}
	}, [open]);

	function handleSubmit() {
		setFieldErrors({});
		const result = createApartmentSchema.safeParse({
			street,
			apartmentNumber,
			coldWaterRate,
			hotWaterRate,
			heatRate,
			consumptionRate,
		});
		if (!result.success) {
			toast.error(
				result.error.issues[0]?.message ?? t("common.validationError"),
			);
			return;
		}
		create.mutate(
			{
				district: districtId,
				user: null,
				street: street.trim(),
				apartment_number: apartmentNumber.trim(),
				cold_water_rate: coldWaterRate ? Number(coldWaterRate) : null,
				hot_water_rate: hotWaterRate ? Number(hotWaterRate) : null,
				heat_rate: heatRate ? Number(heatRate) : null,
				consumption_rate: consumptionRate ? Number(consumptionRate) : null,
			},
			{
				onSuccess: () => {
					toast.success(t("apartments.created"));
					onOpenChange(false);
				},
				onError: (error) => {
					if (error.message === "rate_limited") return;
					setFieldErrors(getDrfFieldErrors(error));
					toast.error(t("apartments.createError"));
				},
			},
		);
	}

	const canSubmit =
		street.trim() &&
		apartmentNumber.trim() &&
		!create.isPending &&
		!create.isRateLimited;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>Utwórz lokal</DialogTitle>
					<DialogDescription>Podaj dane nowego lokalu</DialogDescription>
				</DialogHeader>

				<Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
					<TabsList className="w-full">
						<TabsTrigger value="import" className="flex-1">
							{t("apartmentImport.tabImport")}
						</TabsTrigger>
						<TabsTrigger value="manual" className="flex-1">
							{t("apartmentImport.tabManual")}
						</TabsTrigger>
					</TabsList>

					<TabsContent value="import" className="pt-2">
						<ApartmentCsvUpload
							districtId={districtId}
							onSuccess={() => onOpenChange(false)}
						/>
					</TabsContent>

					<TabsContent value="manual" className="pt-2">
						<div className="flex flex-col gap-4">
							<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="apartment-street">Ulica</Label>
									<Input
										id="apartment-street"
										name="street"
										autoComplete="off"
										value={street}
										onChange={(e) => setStreet(e.target.value)}
										placeholder={t("apartments.streetPlaceholder")}
										aria-invalid={!!fieldErrors.street}
									/>
									{fieldErrors.street && (
										<p className="text-destructive text-sm" role="alert">
											{fieldErrors.street}
										</p>
									)}
								</div>
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="apartment-number">Numer lokalu</Label>
									<Input
										id="apartment-number"
										name="apartment-number"
										autoComplete="off"
										value={apartmentNumber}
										onChange={(e) => setApartmentNumber(e.target.value)}
										placeholder={t("apartments.numberPlaceholder")}
										aria-invalid={!!fieldErrors.apartment_number}
									/>
									{fieldErrors.apartment_number && (
										<p className="text-destructive text-sm" role="alert">
											{fieldErrors.apartment_number}
										</p>
									)}
								</div>
							</div>

							<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="cold-water-rate">
										Stawka wody zimnej (zł/m³)
									</Label>
									<Input
										id="cold-water-rate"
										name="cold-water-rate"
										autoComplete="off"
										type="number"
										min="0"
										step="0.01"
										value={coldWaterRate}
										onChange={(e) => setColdWaterRate(e.target.value)}
										placeholder={t("apartments.ratePlaceholder")}
										aria-invalid={!!fieldErrors.cold_water_rate}
									/>
									{fieldErrors.cold_water_rate && (
										<p className="text-destructive text-sm" role="alert">
											{fieldErrors.cold_water_rate}
										</p>
									)}
								</div>
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="hot-water-rate">
										Stawka wody ciepłej (zł/m³)
									</Label>
									<Input
										id="hot-water-rate"
										name="hot-water-rate"
										autoComplete="off"
										type="number"
										min="0"
										step="0.01"
										value={hotWaterRate}
										onChange={(e) => setHotWaterRate(e.target.value)}
										placeholder={t("apartments.ratePlaceholder")}
										aria-invalid={!!fieldErrors.hot_water_rate}
									/>
									{fieldErrors.hot_water_rate && (
										<p className="text-destructive text-sm" role="alert">
											{fieldErrors.hot_water_rate}
										</p>
									)}
								</div>
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="heat-rate">Stawka ciepła (zł/GJ)</Label>
									<Input
										id="heat-rate"
										name="heat-rate"
										autoComplete="off"
										type="number"
										min="0"
										step="0.01"
										value={heatRate}
										onChange={(e) => setHeatRate(e.target.value)}
										placeholder={t("apartments.ratePlaceholder")}
										aria-invalid={!!fieldErrors.heat_rate}
									/>
									{fieldErrors.heat_rate && (
										<p className="text-destructive text-sm" role="alert">
											{fieldErrors.heat_rate}
										</p>
									)}
								</div>
								{!hideElectricity && (
									<div className="flex flex-col gap-1.5">
										<Label htmlFor="energy-rate">Stawka energii (zł/kWh)</Label>
										<Input
											id="energy-rate"
											name="energy-rate"
											autoComplete="off"
											type="number"
											min="0"
											step="0.01"
											value={consumptionRate}
											onChange={(e) => setConsumptionRate(e.target.value)}
											placeholder={t("apartments.ratePlaceholder")}
											aria-invalid={!!fieldErrors.consumption_rate}
										/>
										{fieldErrors.consumption_rate && (
											<p className="text-destructive text-sm" role="alert">
												{fieldErrors.consumption_rate}
											</p>
										)}
									</div>
								)}
							</div>
						</div>
					</TabsContent>
				</Tabs>

				{mode === "manual" && (
					<DialogFooter>
						<Button
							size="sm"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={create.isPending}
						>
							Anuluj
						</Button>
						<Button onClick={handleSubmit} disabled={!canSubmit}>
							{create.isPending ? "Tworzenie…" : "Utwórz"}
						</Button>
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	);
}
